import type { DiretoriaIndicator } from '@/data/diretoriaTypes';

export interface DiretoriaFaturamentoBrutoPayload {
  ytdTotal: number;
  mesAtual: number;
  variacaoPct: number | null;
  mensal: Array<{ mes: number; name: string; totalPago: number }>;
  porEmpresa: Array<{ nomeEmpresa: string; totalPago: number }>;
  querySql?: string;
}

export interface DiretoriaInadimplenciaPayload {
  saldoVencido: number;
  titulosVencidos: number;
  novosMes: number;
  faixas: Array<{ faixa: string; qtd: number; saldo: number }>;
  querySql?: string;
}

export interface DiretoriaInfracoesPayload {
  pagasMes: { qtd: number; valor: number };
  vencidas: { qtd: number; valor: number };
  aVencer: { qtd: number; valor: number };
  querySql?: string;
}

export interface DiretoriaMotosAlugadasPayload {
  ativas: number;
  novasMes: number;
  canceladasMes: number;
  mensal: Array<{ name: string; novas: number; canceladas: number }>;
  variacaoPct?: number | null;
  querySql?: string;
}

export interface DiretoriaRentabilidadePayload {
  receitaTotal: number;
  custoTotal: number;
  motosBase: number;
  mediaReceitaPorMoto: number;
  mediaCustoPorMoto: number;
  margemPct: number | null;
  mensal: Array<{ name: string; receita: number; custo: number }>;
  variacaoPct?: number | null;
  querySql?: string;
}

export interface DiretoriaLocacaoLocalPayload {
  totalLocacoes: number;
  cidadesDistintas: number;
  topCidade: string | null;
  contratosMes: number;
  variacaoPct?: number | null;
  itens: Array<{ bairro: string; cidade: string; quantidade: number }>;
  querySql?: string;
}

const fmtPct = (value: number | null): string => {
  if (value == null || !Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1).replace('.', ',')}%`;
};

const fmtMi = (value: number, formatCurrency: (n: number) => string): string => {
  if (Math.abs(value) >= 1_000_000) {
    return `R$ ${(value / 1_000_000).toFixed(2).replace('.', ',')} mi`;
  }
  return formatCurrency(value);
};

const fmtQtdValor = (qtd: number, valor: number, formatCurrency: (n: number) => string): string =>
  `${qtd} · ${formatCurrency(valor)}`;

function hasChartOrTable(
  series: Array<{ name: string; valor: number; valor2?: number }>,
  tableRows: string[][],
  pieData?: Array<{ name: string; value: number }>
): boolean {
  return series.length > 0 || tableRows.length > 0 || (pieData?.length ?? 0) > 0;
}

export function buildFaturamentoBrutoIndicatorFromErp(
  base: DiretoriaIndicator,
  payload: DiretoriaFaturamentoBrutoPayload,
  formatCurrency: (value: number) => string
): DiretoriaIndicator {
  const series = payload.mensal.map((row) => ({ name: row.name, valor: Math.round(row.totalPago) }));
  const tableRows = payload.porEmpresa
    .slice(0, 20)
    .map((row) => [row.nomeEmpresa, formatCurrency(row.totalPago)]);
  return {
    ...base,
    shortDescription: 'Faturamento bruto (baixas a receber) — ERP FlyStart.',
    primaryKpiIndex: 0,
    kpis: [
      {
        label: 'Anual (YTD)',
        value: fmtMi(payload.ytdTotal, formatCurrency),
        hint: 'Ano de dataTo · ERP FlyStart',
        tone: payload.ytdTotal > 0 ? 'good' : 'neutral',
      },
      {
        label: 'Período / mês ref.',
        value: formatCurrency(payload.mesAtual),
        hint: 'Baixas no intervalo (ou mês de dataTo)',
        tone: payload.mesAtual > 0 ? 'good' : 'neutral',
      },
      {
        label: 'Var. vs período ant.',
        value: fmtPct(payload.variacaoPct),
        hint: 'ERP FlyStart',
        tone: payload.variacaoPct == null ? 'neutral' : payload.variacaoPct >= 0 ? 'good' : 'bad',
      },
    ],
    chartKind: 'line',
    chartTitle: 'Evolução mensal (ano de referência)',
    series,
    barSeriesKey: 'valor',
    seriesLabels: { valor: 'Faturamento' },
    tableHeaders: ['Unidade', 'Faturamento'],
    tableRows,
    footnote:
      'Baixas (contabaixa) tipocontaid=1 com cb.deleted_at IS NULL; CAST(data_baixa AS DATE). Série mensal = ano de dataTo; tabela = empresas no mês de dataTo.',
    querySql: payload.querySql,
    dataSource: 'erp',
    hasData: hasChartOrTable(series, tableRows) || payload.ytdTotal > 0 || payload.mesAtual > 0,
  };
}

export function buildInadimplenciaIndicatorFromErp(
  base: DiretoriaIndicator,
  payload: DiretoriaInadimplenciaPayload,
  formatCurrency: (value: number) => string
): DiretoriaIndicator {
  const series = payload.faixas.map((row) => ({
    name: row.faixa,
    valor: row.qtd,
    valor2: Math.round(row.saldo),
  }));
  const tableRows = payload.faixas.map((row) => [row.faixa, String(row.qtd), formatCurrency(row.saldo)]);
  return {
    ...base,
    shortDescription: 'Contas a receber vencidas com saldo em aberto (ERP FlyStart).',
    primaryKpiIndex: 0,
    kpis: [
      {
        label: 'Saldo vencido',
        value: formatCurrency(payload.saldoVencido),
        hint: 'As-of dataTo',
        tone: payload.saldoVencido > 0 ? 'bad' : 'good',
      },
      {
        label: 'Títulos vencidos',
        value: String(payload.titulosVencidos),
        hint: 'As-of dataTo',
        tone: payload.titulosVencidos > 0 ? 'bad' : 'good',
      },
      {
        label: 'Novos no mês',
        value: String(payload.novosMes),
        hint: 'Vencimento no mês de dataTo',
        tone: payload.novosMes > 0 ? 'bad' : 'good',
      },
    ],
    chartKind: 'barGrouped',
    chartTitle: 'Aging por faixa de atraso',
    series,
    seriesLabels: { valor: 'Qtd.', valor2: 'Saldo' },
    tableHeaders: ['Faixa de atraso', 'Qtd.', 'Saldo'],
    tableRows,
    footnote:
      'As-of dataTo. Apenas locações APROVADA (locacao.status_atual). Exclui multas por heurística LIKE; saldo = totalliquido − baixas (cb.deleted_at IS NULL). Aging 0–30 / 31–60 / 61+.',
    querySql: payload.querySql,
    dataSource: 'erp',
    hasData:
      hasChartOrTable(series, tableRows) ||
      payload.saldoVencido > 0 ||
      payload.titulosVencidos > 0,
  };
}

export function buildInfracoesIndicatorFromErp(
  base: DiretoriaIndicator,
  payload: DiretoriaInfracoesPayload,
  formatCurrency: (value: number) => string
): DiretoriaIndicator {
  const media = (qtd: number, valor: number) => (qtd > 0 ? valor / qtd : 0);
  const row = (estado: string, qtd: number, valor: number) => [
    estado,
    String(qtd),
    formatCurrency(valor),
    formatCurrency(media(qtd, valor)),
  ];
  const series = [
    { name: 'Pagas (mês ref.)', valor: payload.pagasMes.qtd, valor2: Math.round(payload.pagasMes.valor) },
    { name: 'Vencidas', valor: payload.vencidas.qtd, valor2: Math.round(payload.vencidas.valor) },
    { name: 'A vencer', valor: payload.aVencer.qtd, valor2: Math.round(payload.aVencer.valor) },
  ];
  const tableRows = [
    row('Pagas (mês ref.)', payload.pagasMes.qtd, payload.pagasMes.valor),
    row('Vencidas', payload.vencidas.qtd, payload.vencidas.valor),
    row('A vencer', payload.aVencer.qtd, payload.aVencer.valor),
  ];
  const totalQtd = payload.pagasMes.qtd + payload.vencidas.qtd + payload.aVencer.qtd;
  const totalValor = payload.pagasMes.valor + payload.vencidas.valor + payload.aVencer.valor;
  return {
    ...base,
    shortDescription: 'Contas de multa (multa_id) por estado — ERP FlyStart.',
    primaryKpiIndex: 0,
    kpis: [
      {
        label: 'Pagas (mês ref.)',
        value: fmtQtdValor(payload.pagasMes.qtd, payload.pagasMes.valor, formatCurrency),
        hint: 'Baixas no ano/mês de dataTo',
        tone: 'good',
      },
      {
        label: 'Vencidas',
        value: fmtQtdValor(payload.vencidas.qtd, payload.vencidas.valor, formatCurrency),
        hint: 'Vencimento < dataTo',
        tone: payload.vencidas.qtd > 0 ? 'bad' : 'good',
      },
      {
        label: 'Média / multa',
        value: formatCurrency(media(totalQtd, totalValor)),
        hint: 'Valor total ÷ qtd. (todos os estados)',
        tone: 'neutral',
      },
    ],
    chartKind: 'barGrouped',
    chartTitle: 'Multas por estado',
    series,
    seriesLabels: { valor: 'Qtd.', valor2: 'Valor' },
    tableHeaders: ['Estado', 'Qtd.', 'Valor', 'Média'],
    tableRows,
    footnote:
      'Filtro c.multa_id IS NOT NULL. Saldo: parcela=1 → valorparcela, senão totalliquido − baixas (deleted_at). Pagas = baixas no ano/mês de dataTo. Média = valor ÷ qtd.',
    querySql: payload.querySql,
    dataSource: 'erp',
    hasData: totalQtd > 0,
  };
}

export function buildMotosAlugadasIndicatorFromErp(
  base: DiretoriaIndicator,
  payload: DiretoriaMotosAlugadasPayload
): DiretoriaIndicator {
  const isPeriodMode = payload.mensal.length === 0;
  const series = isPeriodMode
    ? [
        { name: 'Novas', valor: payload.novasMes },
        { name: 'Canceladas', valor: payload.canceladasMes },
      ]
    : payload.mensal.map((row) => ({ name: row.name, valor: row.novas, valor2: row.canceladas }));
  const tableRows = isPeriodMode
    ? [
        ['Novas no período', String(payload.novasMes)],
        ['Canceladas no período', String(payload.canceladasMes)],
        ['Alugadas (frota)', String(payload.ativas)],
      ]
    : payload.mensal.slice(-2).map((row) => [row.name, String(row.novas), String(row.canceladas)]);
  return {
    ...base,
    shortDescription: 'Frota alugada e fluxo de locações (ERP FlyStart).',
    primaryKpiIndex: 0,
    kpis: [
      {
        label: 'Alugadas (frota)',
        value: String(payload.ativas),
        hint: 'Snapshot · status_equipamento=alugada',
        tone: payload.ativas > 0 ? 'good' : 'neutral',
      },
      {
        label: isPeriodMode ? 'Novas no período' : 'Novas no mês',
        value: String(payload.novasMes),
        hint: 'Início de contrato',
        tone: payload.novasMes > 0 ? 'good' : 'neutral',
      },
      {
        label: isPeriodMode ? 'Var. vs período ant.' : 'Canceladas no mês',
        value: isPeriodMode ? fmtPct(payload.variacaoPct ?? null) : String(payload.canceladasMes),
        hint: isPeriodMode ? 'Novas locações' : 'data_cancelamento',
        tone: isPeriodMode
          ? payload.variacaoPct == null
            ? 'neutral'
            : payload.variacaoPct >= 0
              ? 'good'
              : 'bad'
          : 'neutral',
      },
    ],
    chartKind: isPeriodMode ? 'bar' : 'barGrouped',
    chartTitle: isPeriodMode ? 'Novas vs canceladas (período)' : 'Entradas e saídas mensais',
    series,
    barSeriesKey: 'valor',
    seriesLabels: { valor: 'Novas', valor2: 'Canceladas' },
    tableHeaders: isPeriodMode ? ['Indicador', 'Valor'] : ['Mês', 'Entradas', 'Saídas'],
    tableRows,
    footnote:
      'Frota alugada = snapshot (equipamentos). Entradas/saídas pela tabela locacao (início e cancelamento no período quando filtrado).',
    querySql: payload.querySql,
    dataSource: 'erp',
    hasData: hasChartOrTable(series, tableRows) || payload.ativas > 0,
  };
}

export function buildRentabilidadeIndicatorFromErp(
  base: DiretoriaIndicator,
  payload: DiretoriaRentabilidadePayload,
  formatCurrency: (value: number) => string
): DiretoriaIndicator {
  const series = payload.mensal.map((row) => ({
    name: row.name,
    valor: Math.round(row.receita),
    valor2: Math.round(row.custo),
  }));
  const tableRows = [
    ['Receita', formatCurrency(payload.receitaTotal)],
    ['Custo', formatCurrency(payload.custoTotal)],
    ['Margem %', fmtPct(payload.margemPct)],
    ['Motos (base)', String(payload.motosBase)],
  ];
  return {
    ...base,
    shortDescription: 'Receita (vw_financeiro loc+OS) vs custo atual por moto — snapshot ERP.',
    primaryKpiIndex: 2,
    kpis: [
      {
        label: 'Média receita / moto',
        value: `${formatCurrency(payload.mediaReceitaPorMoto)} / moto`,
        hint: 'Snapshot · vw_financeiro RECEBER',
        tone: 'neutral',
      },
      {
        label: 'Média custo / moto',
        value: `${formatCurrency(payload.mediaCustoPorMoto)} / moto`,
        hint: 'Snapshot · custo atual',
        tone: payload.mediaCustoPorMoto < 0 ? 'bad' : 'neutral',
      },
      {
        label: 'Margem',
        value: fmtPct(payload.margemPct),
        hint: 'Receita − |custo|',
        tone:
          payload.margemPct == null ? 'neutral' : payload.margemPct >= 15 ? 'good' : 'neutral',
      },
    ],
    chartKind: 'line',
    chartTitle: 'Receita vs custo (mensal)',
    series,
    seriesLabels: { valor: 'Receita', valor2: 'Custo' },
    tableHeaders: ['Componente', 'Valor'],
    tableRows,
    footnote:
      'Receita snapshot via vw_financeiro (locação + OS, tipoconta RECEBER). Custo = CTE canônica (compra, OS, pagar). Sem filtro de data na receita nesta entrega.',
    querySql: payload.querySql,
    dataSource: 'erp',
    hasData:
      hasChartOrTable(series, tableRows) ||
      payload.receitaTotal !== 0 ||
      payload.custoTotal !== 0,
  };
}

export function buildLocacaoLocalIndicatorFromErp(
  base: DiretoriaIndicator,
  payload: DiretoriaLocacaoLocalPayload
): DiretoriaIndicator {
  const byCidade = new Map<string, number>();
  for (const item of payload.itens) {
    byCidade.set(item.cidade, (byCidade.get(item.cidade) ?? 0) + item.quantidade);
  }
  const chartRows = [...byCidade.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([name, valor]) => ({ name, valor }));
  const tableRows = payload.itens.slice(0, 30).map((row) => [row.bairro, row.cidade, String(row.quantidade)]);

  return {
    ...base,
    shortDescription: 'Locações iniciadas no período, por bairro e cidade (ERP FlyStart).',
    primaryKpiIndex: 1,
    kpis: [
      {
        label: 'Cidades',
        value: String(payload.cidadesDistintas),
        hint: 'ERP FlyStart',
        tone: 'neutral',
      },
      {
        label: 'Top cidade',
        value: payload.topCidade ?? '—',
        hint: 'ERP FlyStart',
        tone: 'neutral',
      },
      {
        label: payload.variacaoPct != null ? 'Var. vs período ant.' : 'Contratos (período)',
        value: payload.variacaoPct != null ? fmtPct(payload.variacaoPct) : String(payload.contratosMes),
        hint: 'Novas locações no intervalo',
        tone:
          payload.variacaoPct != null
            ? payload.variacaoPct >= 0
              ? 'good'
              : 'bad'
            : payload.contratosMes > 0
              ? 'good'
              : 'neutral',
      },
    ],
    chartKind: 'barHorizontal',
    chartTitle: 'Top cidades',
    series: chartRows,
    barSeriesKey: 'valor',
    seriesLabels: { valor: 'Locações' },
    tableHeaders: ['Bairro', 'Cidade', 'Locações'],
    tableRows,
    footnote: 'Join locacao + pessoa_crm (bairro/cidade); contratos iniciados no período (data_inicio_contrato).',
    querySql: payload.querySql,
    dataSource: 'erp',
    hasData: hasChartOrTable(chartRows, tableRows) || payload.totalLocacoes > 0,
  };
}
